/**
 * Loacal storage wrapper
 */
const STORAGE = window.localStorage

const USERS_SUPER_LIGHT_VIEW = 'UserSuperLightView'
const USERS_SUPER_LIGHT_SIZE = 'UserSuperLightSize'
const USERS_COMPACT_VIEW = 'usersCompactView'
const USERS_SORT = 'usersSort'
const SIPUA_VOLUME = 'sipuaVolume'
const PANE_USERS = 'paneUsersOn'
const PANE_VMAIL = 'paneVMailOn'
const DEVICE_INPUT = 'deviceInput'
const DEVICE_OUTPUT = 'deviceOutput'
const DEVICE_RINGER = 'deviceRingback' // This should change to 'deviceRinger'
const DEVICE_RINGER_OVERRIDE = 'devRingbackEnabled' // This should change to 'devRingerOverride' (it doesn't enable/disable the ringer, rather flags whether the default device override is enabled)
const RINGING_TONE = 'ringingTone'
const RINGING_VOLUME = 'ringingVolume'
const LOCALE = 'locale'

const CALLS_MISSED = (userId) => `missedCallsCount_${userId}`
const CALLS_HISTORY = (userId) => `hist_${userId}`

// Queue Reports
const REPORT_TYPE = 'reportType'
const REPORT_QUEUES = 'reportQueues'
const REPORT_AGENTS = 'reportAgents'
const REPORT_RANGE = 'reportRange'
const REPORT_START = 'reportStart'
const REPORT_END = 'reportEnd'
const REPORT_TIMEZONE = 'reportTimezone'
const REPORT_LAST_EXECUTED = 'reportLastExecuted'

// Legacy keys, used for migrating legacy data
const LEGACY_CALLS_MISSED = 'missedCallsCount'
const LEGACY_CALLS_HISTORY = 'hist'
const LEGACY_DEVICE_RINGER = 'deviceRingback'
const LEGACY_DEVICE_RINGER_OVERRIDE = 'devRingbackEnabled'

const set = (key, val) => {
  if (STORAGE) {
    STORAGE.setItem(key, val)
  }
}

const get = key => {
  return STORAGE ? STORAGE.getItem(key) : null
}

const remove = key => {
  if (STORAGE) {
    STORAGE.removeItem(key)
  }
}

const getMediaDev = key => {
  const devId = get(key)
  if (!devId) {
    set(key, 'default')
    return 'default'
  }
  return devId
}

const matchDev = (devList, devType) => {
  const devId = getMediaDev(devType)
  const devs = devList.find(d => d.id === devId)
  if (devs) {
    return devs
  }
  return { id: 'default', label: 'Default' }
}

class LocalStorage {
  usersSort () {
    return get(USERS_SORT)
  }

  setUsersSort (sort) {
    set(USERS_SORT, sort)
  }

  usersSuperLightSize () {
    return parseInt(get(USERS_SUPER_LIGHT_SIZE))
  }

  setUsersSuperLightSize (size) {
    set(USERS_SUPER_LIGHT_SIZE, size.toString())
  }

  setUsersSuperLightView (enabled) {
    set(USERS_SUPER_LIGHT_VIEW, Boolean(enabled))
  }

  isUsersSuperLightViewOn () {
    return get(USERS_SUPER_LIGHT_VIEW) === 'true'
  }

  setUsersCompactView (enabled) {
    set(USERS_COMPACT_VIEW, Boolean(enabled))
  }

  isUsersCompactViewOn () {
    return get(USERS_COMPACT_VIEW) === 'true'
  }

  sipuaVolume () {
    const vol = parseFloat(get(SIPUA_VOLUME))
    return (isNaN(vol) || vol < 0 || vol > 1.0)
      ? 1.0
      : vol
  }

  setSipuaVolume (volume) {
    set(SIPUA_VOLUME, volume)
  }

  addCallMissed (userId) {
    const calls = this.callsMissed(userId)
    set(CALLS_MISSED(userId), calls + 1)
  }

  callsMissed (userId) {
    let val = get(CALLS_MISSED(userId))
    if (val === null) {
      val = this.migrateLegacyCallsMissed(userId)
    }
    return ~~val
  }

  // Migrate legacy missed calls to new storage key
  migrateLegacyCallsMissed (userId) {
    const val = get(LEGACY_CALLS_MISSED)
    if (val !== null) {
      set(CALLS_MISSED(userId), ~~val)
    }
    return val
  }

  resetCallsMissed (userId) {
    set(CALLS_MISSED(userId), 0)
  }

  callsHistory (userId) {
    let val = get(CALLS_HISTORY(userId))
    if (val === null) {
      val = this.migrateLegacyCallsHistory(userId)
    }
    return val
  }

  // Migrate legacy calls history to new storage key
  migrateLegacyCallsHistory (userId) {
    const val = get(LEGACY_CALLS_HISTORY)
    if (val !== null) {
      set(CALLS_HISTORY(userId), val)
    }
    return val
  }

  // Migrate legacy 'deviceRingback' to 'deviceRinger'
  migrateLegacyDeviceRinger () {
    const val = getMediaDev(LEGACY_DEVICE_RINGER)
    if (val !== null) {
      set(DEVICE_RINGER, val)
    }
    return val
  }

  // Migrate legacy 'devRingbackEnabled' to 'devRingerOverride'
  migrateLegacyDeviceRingerOverride () {
    const val = Boolean(get(LEGACY_DEVICE_RINGER_OVERRIDE))
    if (val !== null) {
      set(DEVICE_RINGER_OVERRIDE, val)
    }
  }

  setHistory (userId, history) {
    set(CALLS_HISTORY(userId), history)
  }

  isPaneUsersOn () {
    return get(PANE_USERS) === 'true'
  }

  setPaneUsers (enabled) {
    set(PANE_USERS, Boolean(enabled))
  }

  isPaneVMOn () {
    return get(PANE_VMAIL) === 'true'
  }

  setPaneVM (enabled) {
    set(PANE_VMAIL, Boolean(enabled))
  }

  getLocale () {
    return get(LOCALE)
  }

  setLocale (locale) {
    set(LOCALE, locale)
  }

  resetLocale () {
    remove(LOCALE)
  }

  getDeviceInput () {
    return getMediaDev(DEVICE_INPUT)
  }

  setDeviceInput (deviceId) {
    set(DEVICE_INPUT, deviceId)
  }

  getDeviceOutput () {
    return getMediaDev(DEVICE_OUTPUT)
  }

  setDeviceOutput (deviceId) {
    set(DEVICE_OUTPUT, deviceId)
  }

  setRingingTone (tone) {
    set(RINGING_TONE, tone)
  }

  setRingingVolume (volume) {
    if (isNaN(volume) || volume < 0 || volume > 1.0) {
      volume = 0.6
    }
    set(RINGING_VOLUME, volume)
  }

  getDeviceRingback () {
    let val = getMediaDev(DEVICE_RINGER)
    if (val === null) {
      val = this.migrateLegacyDeviceRinger()
    }
    return val
  }

  setDeviceRinger (deviceId) {
    set(DEVICE_RINGER, deviceId)
  }

  matchInputDevice (devList) {
    return matchDev(devList, DEVICE_INPUT)
  }

  matchOutputDevice (devList) {
    return matchDev(devList, DEVICE_OUTPUT)
  }

  matchRingerDevice (devList) {
    if (this.isDevRingerOverrideEnabled()) {
      return matchDev(devList, DEVICE_RINGER)
    }
    return matchDev(devList, DEVICE_OUTPUT)
  }

  setRingerDevEnabled (enabled) {
    if (enabled) {
      set(DEVICE_RINGER_OVERRIDE, true)
    } else {
      remove(DEVICE_RINGER_OVERRIDE)
    }
  }

  isDevRingerOverrideEnabled () {
    let val = Boolean(get(DEVICE_RINGER_OVERRIDE))
    if (val === null) {
      val = this.migrateLegacyDeviceRingerOverride()
    }
    return val
  }

  ringingTone () {
    return get(RINGING_TONE) || 'Ring8'
  }

  ringingVolume () {
    const volume = parseFloat(get(RINGING_VOLUME))
    return (isNaN(volume) || volume < 0 || volume > 1.0)
      ? 0.6
      : volume
  }

  getReportFilters () {
    return {
      type: get(REPORT_TYPE),
      queues: get(REPORT_QUEUES)?.split(','),
      agents: get(REPORT_AGENTS),
      range: get(REPORT_RANGE),
      start: get(REPORT_START),
      end: get(REPORT_END),
      timezone: get(REPORT_TIMEZONE),
      lastExecuted: get(REPORT_LAST_EXECUTED)
    }
  }

  setReportFilters (data) {
    if (data.type !== undefined) set(REPORT_TYPE, data.type); else remove(REPORT_TYPE)
    if (data.queues !== undefined) set(REPORT_QUEUES, data.queues.join(',')); else remove(REPORT_QUEUES)
    if (data.agents !== undefined) set(REPORT_AGENTS, data.agents); else remove(REPORT_AGENTS)
    if (data.range !== undefined) set(REPORT_RANGE, data.range); else remove(REPORT_RANGE)
    if (data.start !== undefined) set(REPORT_START, data.start); else remove(REPORT_START)
    if (data.end !== undefined) set(REPORT_END, data.end); else remove(REPORT_END)
    if (data.timezone !== undefined) set(REPORT_TIMEZONE, data.timezone); else remove(REPORT_TIMEZONE)
    if (data.lastExecuted !== undefined) set(REPORT_LAST_EXECUTED, data.lastExecuted); else remove(REPORT_LAST_EXECUTED)
  }
}

export default new LocalStorage()
